import { reflectKeys } from "@utils/key.reflactor.ts";
import { getValueOrDefault, isEmpty, isNull } from "@utils/var.check.ts";
import {
  Location,
  ResourceDomain,
  ResourceSearchType,
  RestrictSearchParams,
  SearchFilterCountRequest,
  SearchFilterCountResponse,
  SearchFilterRequest,
} from "@modules/resource-search/resource-search-types.ts";
import SearchUtils from "@modules/search/service/search.utils.ts";
import { QuestionType } from "@/types/question";
import { FilterAccordionOption } from "@/common/filter-types";
import { translate } from "@/utils/i18n/locale.utils";
import AudioRecordingThumbnail from "@images/lesson/Question/AudioRecording.svg";
import ClickAndDropThumbnail from "@images/lesson/Question/ClickAndDrop.svg";
import ErrorEditingThumbnail from "@images/lesson/Question/ErrorEditing.svg";
import FillInTheBlankThumbnail from "@images/lesson/Question/FillInTheBlank.svg";
import FreeResponseThumbnail from "@images/lesson/Question/FreeResponse.svg";
import MultipleChoiceThumbnail from "@images/lesson/Question/MultipleChoice.svg";
import MultiplePartThumbnail from "@images/lesson/Question/MultiPartQuestion.svg";
import MultipleResponseThumbnail from "@images/lesson/Question/MultipleResponse.svg";
import { MEDIA_TYPE, MediaType } from "@/plugins/media/media.constant";
import { RouteRecordNameGeneric } from "vue-router";

// Constants
export const SEARCH_RESULT_PAGE_SIZE = 20;
export const BROWSE_RESULT_PAGE_SIZE = 12;

// match with search filter count API returned field names
export const FILTER_OPTIONS_KEY = {
  SUBJECTS: "subjects",
  LEVELS: "levelAndStreams",
  QUESTION_TYPES: "questionTypes",
  MEDIA_OBJECT_TYPES: "mediaObjectTypes",
  RESOURCE_DOMAINS: "resourceDomains",
};

export const RESOURCE_SEARCH_TYPE = {
  LESSON: "LESSON",
  QUESTION: "QUESTION",
  MEDIA: "MEDIA",
  RUBRIC: "RUBRIC",
  APP: "APP",
  WEBSITE: "WEBSITE",
} as const;

export const TRACKABLE_CARD_ACTIONS = [
  "click",
  "openInNewTab",
  "vote",
  "viewReviews",
  "copy",
  "download",
] as const;

export const RESOURCE_SEARCH_OBJECT_TYPE_MAP = {
  [RESOURCE_SEARCH_TYPE.LESSON]: "module",
  [RESOURCE_SEARCH_TYPE.QUESTION]: "question",
  [RESOURCE_SEARCH_TYPE.MEDIA]: "media-object",
  [RESOURCE_SEARCH_TYPE.WEBSITE]: "website",
  [RESOURCE_SEARCH_TYPE.APP]: "app",
} as const;

export const RESOURCE_STATUS = {
  APPROVED: "APPROVED",
  DRAFT: "DRAFT",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  PENDING_UNPUBLISH: "PENDING_UNPUBLISH",
  REJECTED: "REJECTED",
  UNPUBLISHED: "UNPUBLISHED",
} as const;

export const FILTER_OPTIONS = [
  {
    key: FILTER_OPTIONS_KEY.LEVELS,
    label: "search_filter_label_level",
    field: "level", // match with SearchFilterRequest field
    resources: [
      RESOURCE_SEARCH_TYPE.LESSON,
      RESOURCE_SEARCH_TYPE.QUESTION,
      RESOURCE_SEARCH_TYPE.RUBRIC,
    ],
  },
  {
    key: FILTER_OPTIONS_KEY.SUBJECTS,
    label: "search_filter_label_subject",
    field: "subject", // match with SearchFilterRequest field
    resources: [
      RESOURCE_SEARCH_TYPE.LESSON,
      RESOURCE_SEARCH_TYPE.QUESTION,
      RESOURCE_SEARCH_TYPE.RUBRIC,
    ],
  },
  {
    key: FILTER_OPTIONS_KEY.QUESTION_TYPES,
    label: "search_filter_label_question_type",
    field: "resourceType", // match with SearchFilterRequest field
    resources: [RESOURCE_SEARCH_TYPE.QUESTION],
  },
  {
    key: FILTER_OPTIONS_KEY.MEDIA_OBJECT_TYPES,
    label: "search_filter_label_resource_type",
    field: "resourceType", // match with SearchFilterRequest field
    resources: [RESOURCE_SEARCH_TYPE.MEDIA],
  },
  {
    key: FILTER_OPTIONS_KEY.RESOURCE_DOMAINS,
    label: "search_filter_label_location",
    field: "location", // match with SearchFilterRequest field
    resources: [
      RESOURCE_SEARCH_TYPE.LESSON,
      RESOURCE_SEARCH_TYPE.QUESTION,
      RESOURCE_SEARCH_TYPE.RUBRIC,
      RESOURCE_SEARCH_TYPE.MEDIA,
    ],
  },
];

const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  AUDIO: "search_media_type_audio_label",
  EXCEL: "search_media_type_excel_label",
  HTML5: "search_media_type_interactive_label",
  IMAGE: "search_media_type_image_label",
  OTHERS: "search_media_type_others_label",
  PDF: "search_media_type_pdf_label",
  POWERPOINT: "search_media_type_ppt_label",
  VIDEO: "search_media_type_video_label",
  WORD: "search_media_type_word_label",
  ZIP: "search_media_type_zip_label",
};

const MEDIA_TYPE_ICONS: Record<MediaType, string> = {
  AUDIO: "Audio32",
  EXCEL: "Excel32",
  HTML5: "Interactive32",
  IMAGE: "Image32",
  OTHERS: "Folder32",
  PDF: "PDF32",
  POWERPOINT: "Powerpoint32",
  VIDEO: "Video32",
  WORD: "Word32",
  ZIP: "Folder32",
};

const QUESTION_THUMBNAILS = {
  DND: ClickAndDropThumbnail,
  EEQ: ErrorEditingThumbnail,
  FILE: FreeResponseThumbnail,
  FILL: FillInTheBlankThumbnail,
  FREE: FreeResponseThumbnail,
  MCQ: MultipleChoiceThumbnail,
  MPQ: MultiplePartThumbnail,
  MRQ: MultipleResponseThumbnail,
  REC: AudioRecordingThumbnail,
};

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  DND: "search_question_type_dnd_label",
  EEQ: "search_question_type_eeq_label",
  FILE: "search_question_type_fsq_label",
  FILL: "search_question_type_fitb_label",
  FREE: "search_question_type_frq_label",
  MCQ: "search_question_type_mcq_label",
  MPQ: "search_question_type_mpq_label",
  MRQ: "search_question_type_mrq_label",
  REC: "search_question_type_arq_label",
};

const RESOURCE_DOMAIN_LABELS: Record<ResourceDomain, string> = {
  COMMUNITY: "community_gallery_label",
  MOE: "moe_library_label",
  PERSONAL: "my_drive_label",
};

const RESOURCE_SEARCH_TYPE_LABELS: Partial<Record<ResourceSearchType, string>> = {
  APP: "app_library_label",
  WEBSITE: "external_resources_label",
};

export const FILTER_OPTION_SELECTABLE_LABELS: Record<
  ResourceDomain | QuestionType | MediaType,
  string
> = {
  ...RESOURCE_DOMAIN_LABELS,
  ...QUESTION_TYPE_LABELS,
  ...MEDIA_TYPE_LABELS,
};

export const SORT_OPTIONS_CONFIG = {
  RELEVANCE: {
    sortBy: "relevance",
    sortByOrder: "descending",
  },
  LATEST: {
    sortBy: "modifiedDate",
    sortByOrder: "descending",
  },
  LESSON_VOTE: {
    sortBy: "lessonVoteCount",
    sortByOrder: "descending",
  },
  POPULARITY: {
    sortBy: "viewCount",
    sortByOrder: "descending",
  },
};

export const SORT_OPTIONS = {
  WEBSITE: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
  ],
  APP: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
  ],
  LESSON: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_most_viewed_label",
      value: SORT_OPTIONS_CONFIG.POPULARITY.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
    {
      name: "search_result_sort_most_voted_label",
      value: SORT_OPTIONS_CONFIG.LESSON_VOTE.sortBy,
    },
  ],
  QUESTION: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
  ],
  MEDIA: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
  ],
  RUBRIC: [
    {
      name: "search_result_sort_relevance_label",
      value: SORT_OPTIONS_CONFIG.RELEVANCE.sortBy,
    },
    {
      name: "search_result_sort_latest_label",
      value: SORT_OPTIONS_CONFIG.LATEST.sortBy,
    },
  ],
};

export const SORT_BY_TO_ORDER = Object.fromEntries(
  Object.values(SORT_OPTIONS_CONFIG).map((cfg) => [cfg.sortBy, cfg.sortByOrder]),
);

export const NAMESPACE = {
  AUTH: "auth",
  APP: "app",
};

export const COPY_ACTION_TYPE = reflectKeys(["COPY_LESSON", "COPY_MOE_LESSON"]);

export const LOCATION_TYPE_VALUE = {
  MINE: "MINE",
  MOE: "MOE",
  SHARE_WITH_ME: "SHARE_WITH_ME",
  COMMUNITY: "COMMUNITY",
  APP: "APP",
  EXTERNAL_RESOURCES: "EXTERNAL_RESOURCES",
} as const;

export const RESOURCE_DOMAIN = {
  COMMUNITY: "COMMUNITY",
  MOE: "MOE",
  PERSONAL: "PERSONAL",
};

export const SORT_BY = ["modifiedDate", "viewCount", "lessonVoteCount", "relevance"];

export const QUESTION_TYPE = reflectKeys([
  "MCQ",
  "MRQ",
  "REC",
  "FREE",
  "FILE",
  "FILL",
  "DND",
  "EEQ",
  "MPQ",
]);

export const RESOURCE_DOMAIN_TO_LOCATION_TYPE_MAP = {
  MOE: [LOCATION_TYPE_VALUE.MOE],
  COMMUNITY: [LOCATION_TYPE_VALUE.COMMUNITY],
  PERSONAL: [LOCATION_TYPE_VALUE.MINE, LOCATION_TYPE_VALUE.SHARE_WITH_ME],
};

export const MEDIA_RESOURCE_TYPE_OPTIONS = [
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.IMAGE], name: MEDIA_TYPE.IMAGE, value: "1" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.VIDEO], name: MEDIA_TYPE.VIDEO, value: "2" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.HTML5], name: MEDIA_TYPE.HTML5, value: "3" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.AUDIO], name: MEDIA_TYPE.AUDIO, value: "4" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.POWERPOINT], name: MEDIA_TYPE.POWERPOINT, value: "5" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.WORD], name: MEDIA_TYPE.WORD, value: "6" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.EXCEL], name: MEDIA_TYPE.EXCEL, value: "7" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.PDF], name: MEDIA_TYPE.PDF, value: "8" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.ZIP], name: MEDIA_TYPE.ZIP, value: "9" },
  { label: MEDIA_TYPE_LABELS[MEDIA_TYPE.OTHERS], name: MEDIA_TYPE.OTHERS, value: "10" },
];

export const QUESTION_RESOURCE_TYPE_OPTIONS = [
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.MCQ], name: QUESTION_TYPE.MCQ, value: "1" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.MRQ], name: QUESTION_TYPE.MRQ, value: "2" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.FILL], name: QUESTION_TYPE.FILL, value: "3" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.DND], name: QUESTION_TYPE.DND, value: "4" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.FREE], name: QUESTION_TYPE.FREE, value: "5" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.FILE], name: QUESTION_TYPE.FILE, value: "6" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.REC], name: QUESTION_TYPE.REC, value: "7" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.EEQ], name: QUESTION_TYPE.EEQ, value: "8" },
  { label: QUESTION_TYPE_LABELS[QUESTION_TYPE.MPQ], name: QUESTION_TYPE.MPQ, value: "9" },
];

// Methods
export const getLocationLabel = (
  resourceDomain: ResourceDomain | null = null,
  searchType: ResourceSearchType | null = null,
): string => {
  if (resourceDomain) {
    return translate(RESOURCE_DOMAIN_LABELS[resourceDomain]);
  }

  if (searchType) {
    return translate(RESOURCE_SEARCH_TYPE_LABELS[searchType]);
  }

  return "";
};

export const getMediaTypeIcon = (mediaType: MediaType): string => {
  return MEDIA_TYPE_ICONS[mediaType] || "Media32";
};

export const getMediaTypeLabel = (mediaType: MediaType): string => {
  return translate(MEDIA_TYPE_LABELS[mediaType]);
};

export const getQuestionThumbnail = (questionType: QuestionType): string => {
  return QUESTION_THUMBNAILS[questionType] || MultipleChoiceThumbnail;
};

export const getQuestionTypeLabel = (questionType: QuestionType): string => {
  return translate(QUESTION_TYPE_LABELS[questionType]);
};

export const getFilterOptions = (
  resource: "LESSON" | "QUESTION" | "MEDIA" | "RUBRIC",
  restriction: RestrictSearchParams,
  filterCountResult: SearchFilterCountResponse,
): FilterAccordionOption[] => {
  const getFilterData = (filterCountResult: SearchFilterCountResponse, key: string) => {
    if (isEmpty(filterCountResult)) {
      return [];
    }

    return (filterCountResult[key] ?? []).map(({ id, name, count }) => ({
      id: String(id),
      label: id ? name : translate(FILTER_OPTION_SELECTABLE_LABELS[name]) || name,
      count: count,
      value: String(id ?? name),
    }));
  };

  return FILTER_OPTIONS.filter(({ key, resources }) => {
    const singleRestriction =
      (key === FILTER_OPTIONS_KEY.RESOURCE_DOMAINS && restriction.restrictLocation?.length === 1) ||
      (key === FILTER_OPTIONS_KEY.MEDIA_OBJECT_TYPES &&
        restriction.restrictMediaType?.length === 1) ||
      (key === FILTER_OPTIONS_KEY.QUESTION_TYPES && restriction.restrictQuestionType?.length === 1);
    return !singleRestriction && resources.includes(resource);
  }).map(({ key, label, field }) => ({
    header: label,
    field: field,
    data: getFilterData(filterCountResult, key),
  }));
};

export const populateFilter = (
  searchFilters: SearchFilterRequest,
  resource: string,
  restriction: RestrictSearchParams,
): SearchFilterRequest => {
  let resourceType = searchFilters.resourceType;
  if (resource === RESOURCE_SEARCH_TYPE.MEDIA && (resourceType?.length ?? 0) === 0) {
    resourceType = restriction.restrictMediaType;
  } else if (resource === RESOURCE_SEARCH_TYPE.QUESTION && (resourceType?.length ?? 0) === 0) {
    resourceType = restriction.restrictQuestionType;
  }
  return {
    ...searchFilters,
    location: searchFilters.location?.length
      ? searchFilters.location
      : restriction.restrictLocation,
    resourceType,
  };
};

export const populateSearchApiData = (
  resource: string,
  searchKeyword: string,
  searchFilters: SearchFilterRequest,
  sortBy: string | null,
  pageNo: number,
  pageSize: number,
  sortDirection = "descending",
) => {
  const getResourceType = () => {
    if (resource === RESOURCE_SEARCH_TYPE.MEDIA || resource === RESOURCE_SEARCH_TYPE.QUESTION) {
      const resourceType = searchFilters.resourceType;
      if (!resourceType || isEmpty(resourceType)) {
        return null;
      }

      const result: string[] = [];
      resourceType.forEach((type) => {
        result.push(type);
      });
      return result;
    }
    return null;
  };

  const getSort = () => {
    const sortColumn = !sortBy || sortBy === SORT_OPTIONS_CONFIG.RELEVANCE.sortBy ? null : sortBy;
    const isEmptyQuery = !SearchUtils.hasSearchKeyword(searchKeyword);
    return {
      sortColumn: isEmptyQuery && isNull(sortColumn) ? "modifiedDate" : sortColumn,
      sortDirection: sortDirection,
    };
  };

  const getLocations = () => {
    if (resource === RESOURCE_SEARCH_TYPE.APP || resource === RESOURCE_SEARCH_TYPE.WEBSITE) {
      return [];
    } else if (isEmpty(searchFilters.location)) {
      return [LOCATION_TYPE_VALUE.MOE, LOCATION_TYPE_VALUE.COMMUNITY];
    }
    const locations = [] as string[];
    for (const index in searchFilters.location) {
      const location = searchFilters.location[index];
      locations.push(...RESOURCE_DOMAIN_TO_LOCATION_TYPE_MAP[location]);
    }
    return locations;
  };

  return {
    cacheable: false,
    draw: 0,
    areaOfSearchOptions: [],
    searchResourceType: resource,
    searchResourceSubTypes: getResourceType(),
    keyword: searchKeyword,
    start: getValueOrDefault(pageNo - 1, 0),
    length: pageSize,
    sortColumn: getSort().sortColumn,
    sortDirection: getSort().sortDirection,
    resourceOwnerTypes: getLocations(),
    subject: searchFilters.subject,
    levelAndStream: searchFilters.level,
    authorAndCollaboratorIds: searchFilters.authors,
    contentMapId: searchFilters.syllabus,
    topicIds: searchFilters.topic,
    parentTopicIds: searchFilters.parentTopic,
    learningOutcomeIds: searchFilters.learningOutcome,
    gradable: searchFilters.gradable,
    fromResourceLibrary: false,
    fromMoeLibrary: false,
    checkCopyRightForMOEResource: false,
  };
};

export const populateSearchFilterCountApiData = (
  resource: ResourceSearchType = RESOURCE_SEARCH_TYPE.LESSON,
  searchKeyword: string,
  searchFilters: SearchFilterRequest,
  restriction: RestrictSearchParams,
): SearchFilterCountRequest => {
  const getLocations = (resourceDomain): Location[] => {
    const locations = [] as Location[];
    for (const index in resourceDomain) {
      const location = resourceDomain[index];
      locations.push(...RESOURCE_DOMAIN_TO_LOCATION_TYPE_MAP[location]);
    }
    return locations;
  };

  return {
    searchResourceType: resource,
    keyword: searchKeyword,
    restriction: {
      resourceOwnerTypes: restriction.restrictLocation
        ? getLocations(restriction.restrictLocation)
        : [],
      mediaObjectTypes: restriction.restrictMediaType ? restriction.restrictMediaType : [],
      questionTypes: restriction.restrictQuestionType ? restriction.restrictQuestionType : [],
    },
    filter: {
      subjectIds: searchFilters.subject?.filter((id) => !isNaN(Number(id))).map(Number),
      levelAndStreamIds: searchFilters.level?.filter((id) => !isNaN(Number(id))).map(Number),
      resourceOwnerTypes: getLocations(searchFilters.location),
      questionTypes:
        resource === RESOURCE_SEARCH_TYPE.QUESTION
          ? (searchFilters.resourceType as QuestionType[])
          : [],
      mediaObjectTypes:
        resource === RESOURCE_SEARCH_TYPE.MEDIA ? (searchFilters.resourceType as MediaType[]) : [],
    },
  };
};

export const getBrowsePageBySearchPageRouteName = (searchPageRouteName: RouteRecordNameGeneric) => {
  // uses current route (i.e. which search page) to decide which browse page to navigate to.
  // opposite of getSearchPageByBrowsePageRouteName.
  switch (searchPageRouteName) {
    case "CommunityGallerySearch":
      return "FindCommunityGallery";
    case "MoeLibrarySearch":
      return "FindMoeLibrary";
    case "ResourceSearch":
      return "FindResource";
    default:
      return "FindResource";
  }
};
